import { useCallback, useEffect, useRef, useState } from "react";
import { audioExt } from "../lib/media";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders, supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../hooks/useWallet";
import { useProfile } from "../hooks/useProfile";
import { useCreateDispatch } from "../hooks/useCreateDispatch";
import { AppHeader } from "../components/AppHeader";
import { SitePicker } from "../components/SitePicker";
import { KnownSiteCard } from "../components/KnownSiteCard";
import { Recorder } from "../components/Recorder";
import { PhotoPicker } from "../components/PhotoPicker";
import { JobCardEditor } from "../components/JobCardEditor";
import { ContactFields } from "../components/ContactFields";
import { SendSheet } from "../components/SendSheet";
import { CreditsSheet } from "../components/CreditsSheet";
import { FAIR_USE_PER_MONTH } from "../lib/pricingConfig";
import { PermissionCard, usePermission } from "../components/PermissionCard";
import { OfflineBanner } from "../components/Pwa";
import { db } from "../lib/db";
import { useOnline, isNetworkError } from "../lib/net";
import { mergeExtraction, summarizeMerge, EMPTY_EDITED } from "../lib/cardMerge";
import { UpdateCardBanner } from "../components/UpdateCardBanner";
import { getSandboxDraft, clearSandboxDraft, deriveTitle } from "../lib/sandboxDraft";

const EMPTY_CARD = { title: "", tasks: [], hazards: [], transcript: "", language: "", access_code: "", access_notes: "" };

export default function NewDispatch() {
  const { user } = useAuth();
  const { wallet, usage, refresh: refreshWallet } = useWallet();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const online = useOnline();
  const { create, busy, errorCode, setErrorCode } = useCreateDispatch({ user, profile, refreshWallet });

  const [pin, setPin] = useState(null);
  const [siteLabel, setSiteLabel] = useState("");
  const [nearby, setNearby] = useState([]);
  const [knownSite, setKnownSite] = useState(null);
  const [optedOut, setOptedOut] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [text, setText] = useState("");
  const [typeMode, setTypeMode] = useState(false);
  const [phase, setPhase] = useState("idle");
  const [card, setCard] = useState(null);
  const [edited, setEdited] = useState(EMPTY_EDITED);
  const [pending, setPending] = useState([]);  // input changes since the last card write: "photo" | "voice" | "text"
  const hasCard = !!card && (!!card.title?.trim() || (card.tasks || []).length > 0);
  const notePending = (kind) => setPending((p) => (p.includes(kind) ? p : [...p, kind]));
  const lastText = useRef("");
  const [contact, setContact] = useState({ phoneRaw: "", phone: "", email: "" });
  const [created, setCreated] = useState(null);
  const [resume, setResume] = useState(null);
  const [micState, setMicState] = usePermission("microphone");
  const [geoState] = usePermission("geolocation");

  const onPin = useCallback((p, label) => { setPin(p); if (label) setSiteLabel(label); }, []);

  const [fromSandbox, setFromSandbox] = useState(false);
  useEffect(() => {
    // Landing-page sandbox hand-off: prefill the real form (explicit user action → replaces any IDB draft). Demo coords are fake → no pin.
    const sb = new URLSearchParams(window.location.search).get("fromSandbox") === "1" ? getSandboxDraft() : null;
    if (sb) {
      setCard({ ...EMPTY_CARD, title: deriveTitle(sb), tasks: [sb.voiceText.task], access_notes: sb.voiceText.access || "", transcript: sb.notes || "" });
      setEdited({ ...EMPTY_EDITED, title: true, tasks: true, access_notes: true });
      if (sb.notes) { setText(sb.notes); setTypeMode(true); lastText.current = sb.notes; }
      setFromSandbox(true); clearSandboxDraft();
      toast.success("Your test job is ready — drop the real pin and send.");
      return;
    }
    db.get("drafts", "current").then((d) => { if (d && (d.text || d.pin || d.card || d.photos?.length || d.audioBlob)) setResume(d); });
  }, []);
  useEffect(() => {  // centre on the user only if location is already allowed (no permission prompt on mount)
    if (!fromSandbox || geoState !== "granted") return;
    const id = setTimeout(() => document.querySelector("[data-testid=geolocate-button]")?.click(), 600);
    return () => clearTimeout(id);
  }, [fromSandbox, geoState]);

  useEffect(() => {
    if (created) return;
    const id = setTimeout(() => {
      if (!text && !pin && !card && !photos.length && !audioBlob && !contact.phoneRaw && !contact.email) return;
      db.put("drafts", { text, pin, siteLabel, card, contact, typeMode, audioBlob, photos: photos.map((p) => p.blob), edited, pending, savedAt: Date.now() }, "current");
    }, 2000);
    return () => clearTimeout(id);
  }, [text, pin, siteLabel, card, contact, typeMode, audioBlob, photos, edited, pending, created]);

  const restore = () => {
    const d = resume; setResume(null);
    setText(d.text || ""); setPin(d.pin || null); setSiteLabel(d.siteLabel || ""); setCard(d.card || null); setContact(d.contact || { phoneRaw: "", phone: "", email: "" });
    setTypeMode(!!d.typeMode || !!d.text); setAudioBlob(d.audioBlob || null); setEdited({ ...EMPTY_EDITED, ...(d.edited || {}) }); setPending(d.pending || []);
    setPhotos((d.photos || []).map((b) => ({ blob: b, url: URL.createObjectURL(b) })));
  };
  const discard = () => { setResume(null); db.del("drafts", "current"); };

  useEffect(() => {
    if (!pin) return;
    supabase.rpc("nearby_sites", { p_lat: pin.lat, p_lng: pin.lng, p_radius_m: 100 }).then(({ data, error }) => {
      if (error) return toast.error(error.message);
      const list = data || [];
      setNearby(list);
      if (!list.length) { setKnownSite(null); setOptedOut(false); return; }
      if (knownSite && !list.some((s) => s.id === knownSite.id)) setKnownSite(null);
      if (!optedOut && (!knownSite || knownSite.id !== list[0].id)) attachSite(list[0]);
    });
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachSite = (s) => {
    setKnownSite(s); setOptedOut(false);
    setSiteLabel((l) => l || s.title);
    setCard((c) => (c ? mergeSite(c, s) : c));
  };

  const mergeSite = (c, s) => ({ ...c, access_code: c.access_code || s.access_code_masked || "", access_notes: c.access_notes || s.access_notes || "", hazards: Array.from(new Set([...(c.hazards || []), ...(s.hazards || [])])) });

  const detachSite = () => { setKnownSite(null); setOptedOut(true); setSiteLabel(""); };

  const extract = async (blob) => {
    // Always send everything captured so far: the voice note (new one replaces the old), ALL photos and the typed text.
    const audio = blob || audioBlob;
    if (blob) setAudioBlob(blob);
    if (!audio && !photos.length && !text.trim()) return toast.error("Say, snap or type something first.");
    setPhase(blob ? "listening" : "writing");
    const fd = new FormData();
    if (audio) fd.append("audio", audio, `note.${audioExt(audio)}`);
    photos.forEach((p, i) => fd.append("photos", p.blob, `photo-${i}.jpg`));
    if (text.trim()) fd.append("text", text.trim());
    try {
      const r = await fetch(`${API}/ai/extract`, { method: "POST", headers: await authHeaders(), body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Extraction failed");
      const had = hasCard;
      setCard((c) => {
        const { card: merged, added } = mergeExtraction(c || EMPTY_CARD, d, edited, { withAudio: !!audio });
        if (had) toast.success(summarizeMerge(added));
        return knownSite ? mergeSite(merged, knownSite) : merged;
      });
      setPending([]); lastText.current = text;
    } catch (e) {
      if (!online || isNetworkError(e)) {
        const words = (text.trim() || "Voice note job").split(/\s+/);
        setCard((c) => ({ ...EMPTY_CARD, ...(c || {}), title: c?.title || words.slice(0, 6).join(" "), tasks: c?.tasks?.length ? c.tasks : (text.trim() ? [text.trim()] : []), provisional: true, transcript: c?.transcript || text.trim() }));
        setPending([]);
        toast("Offline — AI job card will be written when you're back online");
      } else toast.error(e.message);
    }
    setPhase("idle");
  };
  const onRecorded = (blob) => { if (hasCard) { setAudioBlob(blob); notePending("voice"); } else extract(blob); };
  const onPhotos = (next) => { const changed = next.length !== photos.length; setPhotos(next); if (changed) notePending("photo"); };
  const onCardEdit = (next) => {
    setEdited((e) => ({ title: e.title || next.title !== (card?.title ?? ""), tasks: e.tasks || JSON.stringify(next.tasks) !== JSON.stringify(card?.tasks || []), hazards: e.hazards || JSON.stringify(next.hazards) !== JSON.stringify(card?.hazards || []), access_notes: e.access_notes || next.access_notes !== (card?.access_notes ?? "") }));
    setCard(next);
  };

  const needsCrewName = (contact.phone || contact.email) && !contact.crewContactId && contact.saveToCrew !== false && !(contact.contactName || "").trim();
  const canCreate = card && card.title.trim() && pin && !busy && wallet?.subscription_status !== "refunded_read_only";

  const onCreate = async () => {
    if (needsCrewName) return toast.error("Add a name for the new crew member (or turn off “Save to crew”)");
    try {
      const d = await create({ card, contact, pin, siteLabel, knownSite, audioBlob, photos, text, edited });
      if (d) { setCreated(d); db.del("drafts", "current"); toast.success(d.queued ? "Queued — will send when online" : "Job created · 1 credit used"); }
    } catch (e) { if (e.code === "ERROR") toast.error(e.message); }
  };

  return (
    <main className="shell has-fab" data-testid="new-dispatch-page">
      <AppHeader back="/app" />
      <OfflineBanner fromCache={false} />
      {resume && (
        <div className="card mb-5 flex items-center gap-3 fade-up" data-testid="resume-draft">
          <div className="flex-1"><p className="font-bold">Resume draft?</p><p className="text-xs text-muted">Saved {new Date(resume.savedAt).toLocaleTimeString()}</p></div>
          <button type="button" className="balance-pill" onClick={discard} data-testid="resume-discard">Discard</button>
          <button type="button" className="btn-primary w-auto px-4" onClick={restore} data-testid="resume-restore">Resume</button>
        </div>
      )}
      <PermissionCard kind="geolocation" state={geoState} fallback="You can always tap the map to drop a pin." onAsk={() => navigator.geolocation.getCurrentPosition(() => {}, () => {})} />
      <section className="card p-0 overflow-hidden fade-up" data-testid="pin-section">
        <SitePicker pin={pin} onPin={onPin} />
        <div className="p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted font-mono notranslate" translate="no" data-testid="pin-coords">{pin ? `${pin.lat}, ${pin.lng}` : "No pin yet"}</p>
          </div>
          {fromSandbox && !pin && <p className="text-sm text-[#F6C945] mt-3 font-bold" data-testid="sandbox-pin-hint">📍 Drop the real pin for this job — the demo location wasn't real.</p>}
          <input className="field mt-3" placeholder="Site label (e.g. Henderson farm shed)" value={siteLabel} onChange={(e) => setSiteLabel(e.target.value)} data-testid="site-label-input" />
          {nearby[0] && <div className="mt-3"><KnownSiteCard site={knownSite || nearby[0]} more={nearby.length - 1} attached={!!knownSite} onAttach={attachSite} onDetach={detachSite} /></div>}
        </div>
      </section>

      <section className="card mt-5 fade-up" data-testid="capture-section">
        <p className="eyebrow">Capture</p>
        <h2 className="card-title">Talk it through</h2>
        <PermissionCard kind="microphone" state={micState} fallback="No worries — use “Type it instead” below." onAsk={() => navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => { s.getTracks().forEach((t) => t.stop()); setMicState("granted"); }).catch(() => setMicState("denied"))} />
        <div className="mt-4"><Recorder onAudio={onRecorded} disabled={phase !== "idle"} onPermissionDenied={() => { setTypeMode(true); setMicState("denied"); }} /></div>
        <div className="mt-4"><PhotoPicker photos={photos} onChange={onPhotos} disabled={phase !== "idle"} /></div>
        {!typeMode ? (
          <button type="button" className="text-sm text-muted mt-4 underline-offset-4 hover:underline" onClick={() => setTypeMode(true)} data-testid="type-instead-button">Type it instead</button>
        ) : (
          <div className="mt-4">
            <textarea className="field min-h-[90px]" placeholder="Describe the job…" value={text} onChange={(e) => setText(e.target.value)} onBlur={() => { if (hasCard && text.trim() !== lastText.current.trim()) { notePending("text"); lastText.current = text; } }} data-testid="notes-text-input" />
            <button type="button" className="btn-secondary mt-2 flex items-center justify-center gap-2" onClick={() => extract(null)} disabled={phase !== "idle" || (!text.trim() && !photos.length)} data-testid="generate-card-button"><Sparkles size={16} /> Write job card</button>
          </div>
        )}
        {phase !== "idle" && <p className="flex items-center gap-2 text-sm text-[#F6C945] mt-4" data-testid="processing-state"><Loader2 className="animate-spin" size={16} /> {phase === "listening" ? "Listening…" : "Writing job card…"}</p>}
      </section>

      {(pending.length > 0 || (!hasCard && photos.length > 0)) && phase === "idle" && (
        <UpdateCardBanner pending={pending} hasCard={hasCard} onUpdate={() => extract(null)} onDismiss={() => setPending([])} />
      )}

      {card && (
        <section className="card mt-5 fade-up" data-testid="job-card-section">
          <p className="eyebrow">Job card</p>
          <h2 className="card-title mb-4">Check & edit</h2>
          <JobCardEditor card={card} onChange={onCardEdit} />
        </section>
      )}

      {(card || pin) && (
        <section className="card mt-5 fade-up" data-testid="contact-section">
          <p className="eyebrow">Send to</p>
          <h2 className={`card-title ${card ? "mb-4" : "mb-1"}`}>Contractor</h2>
          {!card && <p className="text-xs text-muted mb-4" data-testid="contact-early-hint">Pick who it's for while the job card is being written.</p>}
          <ContactFields contact={contact} onChange={setContact} />
        </section>
      )}

      {card && (
        <button type="button" className="fab fab-wide" onClick={onCreate} disabled={!canCreate} data-testid="create-job-button">
          {busy ? <><Loader2 className="animate-spin" size={18} /> Creating…</> : "⚡ Create job · 1 credit"}
        </button>
      )}
      {!pin && card && <p className="text-center text-xs text-[#F6C945] mt-3" data-testid="pin-required-hint">Drop a pin on the map to create the job.</p>}

      {created && <SendSheet dispatch={created} onStatus={setCreated} onClose={() => navigate(created.queued ? "/app" : `/app/jobs/${created.id}`)} onCancel={() => navigate("/app")} />}
      {errorCode === "FAIR_USE_LIMIT" && <CreditsSheet title="Fair-use limit reached for this month" subtitle={`Solo Pro covers ${FAIR_USE_PER_MONTH} jobs a month. Buy a credit pack to keep dispatching, or wait until ${usage?.resets_at ? new Date(usage.resets_at).toLocaleDateString(undefined, { day: "numeric", month: "long" }) : "the 1st of next month"}.`} onClose={() => setErrorCode("")} />}
      {errorCode === "INSUFFICIENT_CREDITS" && <CreditsSheet title="You're out of credits" subtitle="Grab a pack or go unlimited to keep dispatching." onClose={() => setErrorCode("")} />}
    </main>
  );
}
